import { ALLOCATION_KEYS, KEYS_NEED_LEASE_AGREEMENT, fmtDate, formatLocale, fracNum, num, type AllocationKey } from '@immoaktas/shared';
import { enumLabel } from '@immoaktas/i18n';

/**
 * i18next context for texts to tenants ("Anrede gegenüber Mietern", account setting, default Sie): the plain key holds
 * the Sie wording, the "_du" key the du wording. Same form as the server's PDFs and letters (services/tenantAddress.ts).
 */
export const tenantContext = (a: { defaults?: { tenantAddress?: string } } | null | undefined): 'du' | undefined => (a?.defaults?.tenantAddress === 'du' ? 'du' : undefined);

/** "Dienstag, 6. Oktober 2026" / "Tuesday 6 October 2026" in the UI language. */
export const longDate = (iso: string) => {
  const [y = 1970, m = 1, d = 1] = iso.slice(0, 10).split('-').map(Number);
  return new Intl.DateTimeFormat(formatLocale(), { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }).format(Date.UTC(y, m - 1, d));
};

/** Compact period "01.01.–31.12.2025" (same year) or full dates otherwise. */
export const shortRange = (from: string, to: string) => {
  if (from.slice(0, 4) !== to.slice(0, 4)) return `${fmtDate(from)}–${fmtDate(to)}`;
  const [y = 1970, m = 1, d = 1] = from.slice(0, 10).split('-').map(Number);
  const head = new Intl.DateTimeFormat(formatLocale(), { day: '2-digit', month: '2-digit', timeZone: 'UTC' }).format(Date.UTC(y, m - 1, d));
  return `${head}–${fmtDate(to)}`;
};

/** Number as editable input text in the UI language, without grouping ("62,5" / "62.5"); read back with parseDecimalInput. */
export const inputNum = (n: number | null | undefined) => (n == null ? '' : n.toLocaleString(formatLocale(), { useGrouping: false, maximumFractionDigits: 6 }));

/** Whole days from `from` to `to` (ISO dates, calendar days). */
export const daysBetween = (from: string, to: string) => Math.round((Date.parse(to.slice(0, 10)) - Date.parse(from.slice(0, 10))) / 86_400_000);

/**
 * Share of one statement line in the unit the reader understands: keys weighted by area, units or co-ownership
 * are day-exact products (engine: m² × days) and read as percent ("18,44 %", design/AbrechnungVorschau);
 * person-days and consumption keys keep "value / total" ("1.098 / 4.758", "95 / 620"). `null` without a total.
 */
export const keyShare = (keyValue: string, keyTotal: string, key?: string) => {
  const value = fracNum(keyValue);
  const total = fracNum(keyTotal);
  if (!total || !Number.isFinite(total) || !Number.isFinite(value)) return null;
  const percent = key === 'AREA_DAYS' || key === 'UNIT_DAYS' || key === 'CO_OWNERSHIP_SHARE';
  const pct = new Intl.NumberFormat(formatLocale(), { style: 'percent', minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return { percent, value, total, text: percent ? pct.format(value / total) : `${num(value, 2)} / ${num(total, 2)}` };
};

type TFn = (key: string, opts?: Record<string, unknown>) => string;
/**
 * Options of an allocation key select (§ 556a Abs. 1 BGB, ADR-0019): persons and units carry "nur wenn im Mietvertrag
 * vereinbart", the default is the living area.
 */
export const keyOptions = (t: TFn) =>
  ALLOCATION_KEYS.map((k) => ({ value: k, label: KEYS_NEED_LEASE_AGREEMENT.includes(k) ? `${enumLabel(t, 'allocationKey', k)} (${t('ui.keyLeaseOnly')})` : enumLabel(t, 'allocationKey', k) }));
/** Hint under a key select when the chosen key needs an agreement in the lease. */
export const keyHint = (t: TFn, k: string | null | undefined) => (k && KEYS_NEED_LEASE_AGREEMENT.includes(k as AllocationKey) ? t('ui.keyLeaseOnlyHint') : undefined);
